import type { SavedFilterModel, SavedFilterType } from '@/shared/generated'

import { Link } from '@tanstack/react-router'
import { isEmpty } from 'lodash/fp'
import {
	ArrowUpRightIcon,
	FolderHeartIcon,
	GlobeIcon,
	LockIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSavedFiltersQuery } from '@/entities/report'

import { Button } from '@/shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
} from '@/shared/components/ui/empty'
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { intlFormat } from '@/shared/tools/dates'

function SharedReportsList({
	onItemClick,
	type,
}: {
	onItemClick(item: SavedFilterModel): void
	type: SavedFilterType
}) {
	const { t } = useTranslation()

	const savedFiltersQuery = useSavedFiltersQuery({
		select: (data) => data.filter((item) => item.type === type),
	})
	const isLoading = savedFiltersQuery.isLoading
	const isEmptyData = isEmpty(savedFiltersQuery?.data)
	if (isLoading) {
		return (
			<div className='flex w-full flex-col justify-center'>
				<div className='space-y-1 p-2'>
					<Skeleton className='h-5 w-32' />
					<Skeleton className='h-4 w-full' />
				</div>
				<div className='space-y-1 p-2'>
					<Skeleton className='h-5 w-20' />
					<Skeleton className='h-4 w-40' />
				</div>
				<div className='space-y-1 p-2'>
					<Skeleton className='h-5 w-16' />
					<Skeleton className='h-4 w-full' />
				</div>
			</div>
		)
	}
	if (isEmptyData) {
		return (
			<Empty>
				<EmptyHeader>
					<EmptyDescription>{t('no_data')}</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	return savedFiltersQuery?.data?.map((filterItem) => (
		<DropdownMenuItem
			onClick={() => onItemClick(filterItem)}
			key={filterItem.id}
		>
			<Item size='xs'>
				<ItemContent>
					<ItemTitle>
						{filterItem.name}{' '}
						{!filterItem.is_public ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<span className='cursor-help'>
										<LockIcon className='size-3 text-muted-foreground' />
									</span>
								</TooltipTrigger>
								<TooltipContent>
									{t('report.private.tooltip', {
										defaultValue: 'This report is private',
									})}
								</TooltipContent>
							</Tooltip>
						) : (
							<Tooltip>
								<TooltipTrigger asChild>
									<span className='cursor-help'>
										<GlobeIcon className='size-3 text-muted-foreground' />
									</span>
								</TooltipTrigger>
								<TooltipContent>
									{t('report.public.tooltip', {
										defaultValue: 'This report is public',
									})}
								</TooltipContent>
							</Tooltip>
						)}
					</ItemTitle>

					<ItemDescription>
						{t('general_last_updated')}:{' '}
						{filterItem.updated_at
							? intlFormat(new Date(filterItem.updated_at + 'Z'), {
									timeStyle: 'short',
									dateStyle: 'short',
								})
							: filterItem.created_at
								? intlFormat(new Date(filterItem.created_at + 'Z'), {
										timeStyle: 'short',
										dateStyle: 'short',
									})
								: '-'}
					</ItemDescription>
				</ItemContent>
			</Item>
		</DropdownMenuItem>
	))
}

type SavedReportsDrodownProps = {
	onItemClick(item: SavedFilterModel): void
	type: SavedFilterType
}
export function SavedReportPicker({
	onItemClick,
	type,
}: SavedReportsDrodownProps) {
	const { t } = useTranslation()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button data-testid='saved-filters-btn' size='icon'>
					<FolderHeartIcon />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				data-testid='saved-filters-list-container'
				align='start'
				side='bottom'
				className='w-72 [--radius:0.65rem]'
			>
				<DropdownMenuGroup>
					<SharedReportsList onItemClick={onItemClick} type={type} />
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link to='/p/dashboard/reports'>
						{t('saved_reports.manage_reports', {
							defaultValue: 'Manage reports',
						})}
						<ArrowUpRightIcon className='ml-auto text-muted-foreground' />
					</Link>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
